import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export default function Newsletter({
    newsletters,
}: {
    newsletters: App.Data.NewsletterData[];
}) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const intervalRef = useRef<number | null>(null);

    const total = newsletters.length;
    const clampedIndex = useMemo(() => {
        return ((currentIndex % total) + total) % total;
    }, [currentIndex, total]);

    const stopAutoPlay = useCallback(() => {
        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const startAutoPlay = useCallback(() => {
        stopAutoPlay();
        intervalRef.current = window.setInterval(() => {
            setCurrentIndex((prev) => prev + 1);
        }, 5000);
    }, [stopAutoPlay]);

    useEffect(() => {
        startAutoPlay();
        return stopAutoPlay;
    }, [startAutoPlay, stopAutoPlay]);

    const goTo = (index: number) => {
        setCurrentIndex(index);
        startAutoPlay();
    };

    const next = () => goTo(clampedIndex + 1);
    const prev = () => goTo(clampedIndex - 1);

    const active = newsletters[clampedIndex];

    if (!active) {
        return null;
    }

    return (
        <section className="bg-background">
            <div className="container pb-12">
                <Card className="my-0 border-none bg-transparent py-6 shadow-none">
                    {/* ARROWS + IMAGE */}
                    <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[auto_1fr_auto] sm:gap-4">
                        {/* LEFT ARROW */}
                        <Button
                            type="button"
                            onClick={prev}
                            variant="ghost"
                            aria-label="Previous slide"
                            className="hidden h-10 w-10 sm:flex"
                        >
                            <ChevronLeft />
                        </Button>

                        {/* IMAGE CONTAINER */}
                        <a
                            href={active.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="relative h-[360px] overflow-hidden rounded-lg sm:h-[420px] md:h-[480px]"
                        >
                            <div className="absolute inset-0">
                                <div
                                    className="h-full w-full bg-cover bg-center transition-[background-image] duration-500"
                                    style={{
                                        backgroundImage: `url('${active.imageUrl}')`,
                                    }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
                            </div>

                            {/* CONTENT OVERLAY */}
                            <div className="absolute inset-0 flex flex-col items-center justify-end p-6 sm:p-8">
                                <div className="w-full max-w-2xl text-center">
                                    <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">
                                        {active.title}
                                    </h2>
                                    <p className="mt-2 line-clamp-2 text-white/90">
                                        {active.content}
                                    </p>
                                </div>
                            </div>

                            {/* MOBILE CONTROLS (inside image) */}
                            <div className="absolute inset-0 flex items-center justify-between px-2 sm:hidden">
                                <Button
                                    type="button"
                                    onClick={prev}
                                    variant="ghost"
                                    className="h-10 w-10 text-white hover:bg-white/10"
                                    aria-label="Previous slide"
                                >
                                    <ChevronLeft />
                                </Button>

                                <Button
                                    type="button"
                                    onClick={next}
                                    variant="ghost"
                                    className="h-10 w-10 text-white hover:bg-white/10"
                                    aria-label="Next slide"
                                >
                                    <ChevronRight />
                                </Button>
                            </div>

                            {/* MOBILE DOTS */}
                            <div className="absolute right-0 bottom-3 left-0 flex justify-center gap-2 sm:hidden">
                                {newsletters.map((s, i) => (
                                    <button
                                        key={s.id}
                                        onClick={() => goTo(i)}
                                        className={`h-2 rounded-full transition-all ${
                                            i === clampedIndex
                                                ? 'w-6 bg-white'
                                                : 'w-2 bg-white/60'
                                        }`}
                                        aria-label={`Go to slide ${i + 1}`}
                                    />
                                ))}
                            </div>
                        </a>

                        {/* RIGHT ARROW */}
                        <Button
                            type="button"
                            onClick={next}
                            variant="ghost"
                            aria-label="Next slide"
                            className="hidden h-10 w-10 sm:flex"
                        >
                            <ChevronRight />
                        </Button>
                    </div>

                    {/* DOTS */}
                    <div className="mt-4 hidden items-center justify-center gap-2 sm:flex">
                        {newsletters.map((s, i) => (
                            <button
                                key={s.id}
                                onClick={() => goTo(i)}
                                className={`h-2 rounded-full transition-all ${
                                    i === clampedIndex
                                        ? 'w-6 bg-foreground'
                                        : 'w-2 bg-foreground/40'
                                }`}
                                aria-label={`Go to slide ${i + 1}`}
                            />
                        ))}
                    </div>
                </Card>
            </div>
        </section>
    );
}
